import _ from "lodash";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import jwt_decode from "jwt-decode";
import dayjs from "dayjs";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {

    const { t } = useTranslation();
    
    const getAuthTokens = () => {
        try {
            return localStorage.getItem("authTokens")
            ? JSON.parse(localStorage.getItem("authTokens"))
            : null
        } catch (e) {
            console.error("Ocorreu um erro ao ler os seus dados de sessão.");
            localStorage.removeItem("authTokens");
            localStorage.removeItem("user");
            localStorage.removeItem("profile");
        }
    }

    const getUser = () => {
        try {
            return localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null
        } catch (e) {
            console.error("Ocorreu um erro ao ler os seus dados de sessão.");
            localStorage.removeItem("authTokens");
            localStorage.removeItem("user");
            localStorage.removeItem("profile");
        }
    }

    const getProfile = () => {
        try {
            return localStorage.getItem("profile")
            ? JSON.parse(localStorage.getItem("profile"))
            : null
        } catch (e) {
            console.error("Ocorreu um erro ao ler os seus dados de sessão.");
            localStorage.removeItem("authTokens");
            localStorage.removeItem("user");
            localStorage.removeItem("profile");
        }
    }

    // Stores the encoded JWT tokens
    const [authTokens, setAuthTokens] = useState(getAuthTokens());
    const [user, setUser] = useState(getUser());
    const [profile, setProfile] = useState(getProfile());

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const login = async (username, password) => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/${process.env.REACT_APP_API_PATH}/auth/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                username,
                password
            })
        }).catch((e) => {
            console.error("caught error fetching user profile, returning to login page.");
            logout();
            navigate("/login");
        });
        const data = await response.json();

        if (response.status === 200) {
            const tokens = {
                access: data.access,
                refresh: data.refresh
            };
            const userProfile = _.cloneDeep(data)
            setUser(data);
            localStorage.setItem("user", JSON.stringify(data));
            setAuthTokens(tokens);
            localStorage.setItem("authTokens", JSON.stringify(tokens));

            if (userProfile) {
                let perms = [];
                userProfile.groups.map((group) => (perms = [...perms, ...group.permissions]));
                perms = [...perms, ...userProfile.user_permissions];
                userProfile["permissions"] = perms;
                setProfile(userProfile);
                localStorage.setItem("profile", JSON.stringify(userProfile));
            }
            setUser(userProfile);
            
            
            
            //getProfile(data.access);
        } else if (response.status === 401) {
            console.error("Got response status: " + response.status);
            throw new Error(t("error.api.loginNotAuthorized"));
        } else {
            console.error("Got response status: " + response.status);
            throw new Error(t("error.api.loginUnknown"));
        }
    };

    const logout = () => {
        setAuthTokens(null);
        setUser(null);
        setProfile(null);
        localStorage.removeItem("authTokens");
        localStorage.removeItem("user");
        localStorage.removeItem("profile");
        navigate("/home");
    };

    const _validatePermissionGroup = (groupName) => {
        if(groupName.startsWith("!")) {
            return !profile?.groups?.some( (group) => group.name === groupName.substring(1));
        } else {
            return profile?.groups?.some( (group) => group.name === groupName);
        }
    }
    const hasPermissionGroup = (groups) => {
        if(groups instanceof Array) {
            return groups.some((el) => _validatePermissionGroup(el))
        } else {
            return _validatePermissionGroup(groups);
        }
    }

    const validateValidTokens = () => {
        const authTokenData = jwt_decode(authTokens.access);
        const isExpired = dayjs.unix(authTokenData.exp).diff(dayjs()) < 1;

        if (!isExpired) return true;

        const refreshTokenData = jwt_decode(authTokens.refresh);
        const isExpiredRef = dayjs.unix(refreshTokenData.exp).diff(dayjs()) < 1;

        if (isExpiredRef) {
            logout();
        }
    }

    const refreshUserData = (newUser) => {
        let updatedUser = JSON.parse(localStorage.getItem("user"));
        updatedUser.profile = newUser;

        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
    }

    const isClient = () => {
        return hasPermissionGroup("CLIENT");
    }

    const isAgentUser = () => {
        return hasPermissionGroup("ENERGY_AGENT_USER");
    }

    const isIndieAdmin = () => {
        return hasPermissionGroup("INDIE_ADMIN");
    }

    const contextData = {
        user,
        setUser,
        authTokens,
        profile,
        setAuthTokens,
        login,
        logout,
        getProfile,
        hasPermissionGroup,
        refreshUserData,
        isClient,
        isAgentUser,
        isIndieAdmin
    };

    useEffect(() => {
        /** Check if the user is logged and validate the tokens are still valid*/
        if (authTokens) {
            validateValidTokens();
        }
    }, []);

    return (
        <AuthContext.Provider value={contextData}>
            {loading ? null : children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
}